import { create } from 'zustand';
import { apiClient } from '@/api/client';
import type { UsageMap } from './types';

/**
 * Persistent usage tracker for commands and search items. Drives the
 * "smart default" list on empty input. Persists per-user via the existing
 * settings endpoint (`command_usage` key → JSON UsageMap).
 *
 * Writes are debounced — we don't want every command invocation to round-trip
 * to the server. Reads are lazy; `load()` should be called once at app boot.
 */
interface UsageStore {
  commands: UsageMap;
  search: UsageMap;
  loaded: boolean;
  load: () => Promise<void>;
  recordCommand: (id: string) => void;
  recordSearch: (id: string) => void;
}

let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(get: () => UsageStore) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const { commands, search } = get();
    // Fire-and-forget; a failed save just means one more flush attempt later.
    apiClient.put('/api/settings', {
      command_usage: JSON.stringify({ commands, search }),
    }).catch(() => { /* ignore */ });
  }, 2000);
}

export const useUsageStore = create<UsageStore>((set, get) => ({
  commands: {},
  search: {},
  loaded: false,

  async load() {
    if (get().loaded) return;
    try {
      const res = await apiClient.get<Record<string, unknown>>('/api/settings');
      const raw = res?.command_usage;
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw) as { commands?: UsageMap; search?: UsageMap };
        set({
          commands: parsed.commands ?? {},
          search: parsed.search ?? {},
          loaded: true,
        });
        return;
      }
    } catch {
      // Missing / corrupt — start fresh without blowing up
    }
    set({ loaded: true });
  },

  recordCommand(id: string) {
    const now = Date.now();
    set((s) => ({
      commands: {
        ...s.commands,
        [id]: {
          count: (s.commands[id]?.count ?? 0) + 1,
          lastUsedAt: now,
        },
      },
    }));
    scheduleFlush(get);
  },

  recordSearch(id: string) {
    const now = Date.now();
    set((s) => ({
      search: {
        ...s.search,
        [id]: {
          count: (s.search[id]?.count ?? 0) + 1,
          lastUsedAt: now,
        },
      },
    }));
    scheduleFlush(get);
  },
}));
